import { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Collapse,
  Empty,
  Input,
  Modal,
  InputNumber,
  Spin,
  Switch,
  Tag,
} from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import medicalReviewApi, {
  MedicalReviewApiError,
} from "@/services/medicalReviewApi";
import { useAppNotification } from "@/hooks/useAppNotification";
import { fieldLabel } from "./fieldLabels";
import type {
  DomainSettings,
  ReviewSettings as Settings,
} from "./medicalReview.types";

type Draft = {
  domains: Record<string, Set<string>>;
  sla: Record<string, number>;
};

function draftFrom(settings: Settings): Draft {
  return {
    domains: Object.fromEntries(
      settings.domains.map((d) => [
        d.key,
        new Set(d.fields.filter((f) => f.material).map((f) => f.name)),
      ]),
    ),
    sla: Object.fromEntries(
      Object.entries(settings.sla).map(([tier, spec]) => [tier, spec.hours]),
    ),
  };
}

export default function ReviewPolicy({ trialId }: { trialId: string }) {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [reason, setReason] = useState("");
  const [confirming, setConfirming] = useState(false);
  const queryClient = useQueryClient();
  const { openSuccessNotification, openErrorNotification, contextHolder } =
    useAppNotification();

  const settingsQuery = useQuery({
    queryKey: ["medical-review-policy", trialId],
    queryFn: () => medicalReviewApi.policy(trialId),
  });

  const settings = settingsQuery.data;
  const current = useMemo(
    () => (settings ? (draft ?? draftFrom(settings)) : null),
    [settings, draft],
  );

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!settings || !current) throw new Error("Nothing to save");
      return medicalReviewApi.savePolicy(trialId, {
        domains: Object.fromEntries(
          settings.domains.map((d) => [
            d.key,
            { material_fields: [...(current.domains[d.key] ?? [])] },
          ]),
        ),
        sla_hours: current.sla,
        ...(reason.trim() ? { reason_for_change: reason.trim() } : {}),
      });
    },
    onSuccess: (result) => {
      setDraft(null);
      setReason("");
      setConfirming(false);
      queryClient.setQueryData(["medical-review-policy", trialId], result);
      // The queue's ordering and its re-review counts are derived from these
      // definitions, so a stale queue would contradict the panel.
      queryClient.invalidateQueries({
        queryKey: ["medical-review-queue", trialId],
      });
      queryClient.invalidateQueries({
        queryKey: ["medical-review-policy-history", trialId],
      });
      openSuccessNotification({
        title: result.changes.length
          ? "Review settings updated"
          : "No changes to save",
        description: result.changes.length
          ? `${result.changes.join("; ")}. Takes effect at the next scan; records already reviewed are not reopened retrospectively.`
          : "The watch list already matched what you submitted.",
      });
    },
    onError: (error: MedicalReviewApiError) =>
      openErrorNotification({
        title: "Could not update the watch list",
        description: error.message,
      }),
  });

  if (settingsQuery.isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spin />
      </div>
    );
  }
  if (settingsQuery.isError || !settings || !current) {
    return (
      <Empty
        description="The watch list is not available for this trial."
        className="mt-16"
      />
    );
  }

  const editable = settings.can_edit;
  const dirty =
    draft !== null &&
    (settings.domains.some((d) => {
      const chosen = current.domains[d.key] ?? new Set<string>();
      const original = new Set(
        d.fields.filter((f) => f.material).map((f) => f.name),
      );
      return (
        chosen.size !== original.size ||
        [...chosen].some((f) => !original.has(f))
      );
    }) ||
      Object.entries(settings.sla).some(
        ([tier, spec]) => current.sla[tier] !== spec.hours,
      ));

  // A first version has nothing to justify; every later one does, and the
  // backend refuses without it — so ask here rather than failing the save.
  const amendsExisting =
    settings.domains.some((d) => d.source === "policy") ||
    Object.values(settings.sla).some((s) => s.source === "policy");

  const emptyDomains = settings.domains.filter(
    (d) => (current.domains[d.key] ?? new Set()).size === 0,
  );

  // What the confirm modal reports back, in the reader's vocabulary rather
  // than as field names.
  const pendingChanges = [
    ...settings.domains.flatMap((domain) => {
      const chosen = current.domains[domain.key] ?? new Set<string>();
      const added = domain.fields
        .filter((f) => !f.material && chosen.has(f.name))
        .map((f) => fieldLabel(f.name));
      const removed = domain.fields
        .filter((f) => f.material && !chosen.has(f.name))
        .map((f) => fieldLabel(f.name));
      return [
        added.length
          ? `${domain.label}: ${added.join(", ")} now reopens review`
          : "",
        removed.length
          ? `${domain.label}: ${removed.join(", ")} no longer reopens review`
          : "",
      ].filter(Boolean);
    }),
    ...Object.entries(settings.sla)
      .filter(([tier, spec]) => current.sla[tier] !== spec.hours)
      .map(
        ([tier, spec]) =>
          `${tier} response time ${spec.hours}h \u2192 ${current.sla[tier]}h`,
      ),
  ];

  const toggleField = (domainKey: string, field: string, material: boolean) =>
    setDraft(() => {
      const next: Draft = {
        domains: Object.fromEntries(
          Object.entries(current.domains).map(([k, v]) => [k, new Set(v)]),
        ),
        sla: { ...current.sla },
      };
      const set = next.domains[domainKey] ?? new Set<string>();
      if (material) set.add(field);
      else set.delete(field);
      next.domains[domainKey] = set;
      return next;
    });

  const resetDomain = (domain: DomainSettings) =>
    setDraft(() => ({
      domains: {
        ...Object.fromEntries(
          Object.entries(current.domains).map(([k, v]) => [k, new Set(v)]),
        ),
        [domain.key]: new Set(
          domain.fields.filter((f) => f.default_material).map((f) => f.name),
        ),
      },
      sla: { ...current.sla },
    }));

  return (
    <div className="flex flex-col gap-4">
      {contextHolder}

      {!editable && (
        <Alert
          type="warning"
          showIcon
          message="Read-only"
          description="Changing the watch list needs the Rule Studio permission and a Medical Monitor role."
        />
      )}

      <section className="flex flex-col gap-2">
        <SectionTitle>Review deadlines</SectionTitle>
        <div className="flex flex-wrap gap-3">
          {Object.entries(settings.sla).map(([tier, spec]) => (
            <div
              key={tier}
              className="flex min-w-[260px] flex-col gap-1 rounded-lg border border-[#E6ECF1] bg-white px-4 py-3"
            >
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-medium text-[#2B4752]">
                  {tier === "sae" ? "Serious events" : "Routine records"}
                </span>
                <InputNumber
                  size="small"
                  min={1}
                  max={8760}
                  disabled={!editable}
                  value={current.sla[tier]}
                  onChange={(value) =>
                    setDraft(() => ({
                      domains: Object.fromEntries(
                        Object.entries(current.domains).map(([k, v]) => [
                          k,
                          new Set(v),
                        ]),
                      ),
                      sla: {
                        ...current.sla,
                        [tier]: Number(value) || spec.hours,
                      },
                    }))
                  }
                  addonAfter="h"
                  aria-label={`${tier === "sae" ? "Serious events" : "Routine records"} review deadline in hours`}
                  style={{ width: 110 }}
                />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="flex flex-col gap-2">
        <SectionTitle>Watched fields</SectionTitle>
        <Collapse
          items={settings.domains.map((domain) => {
            const chosen = current.domains[domain.key] ?? new Set<string>();
            return {
              key: domain.key,
              label: (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-[#2B4752]">
                    {domain.label}
                  </span>
                  <span className="text-xs text-[#556C75]">
                    {chosen.size} of {domain.fields.length} material
                  </span>
                  {!domain.is_default && <Tag color="blue">Customised</Tag>}
                  {chosen.size === 0 && (
                    <Tag color="error">Nothing reopens review</Tag>
                  )}
                </div>
              ),
              children: (
                <div className="flex flex-col gap-3">
                  <dl className="m-0 grid grid-cols-1 gap-x-8 sm:grid-cols-2">
                    {domain.fields.map((field) => (
                      <div
                        key={field.name}
                        className="flex items-center justify-between gap-3 border-b border-dashed border-[#E6ECF1] py-1.5"
                      >
                        <dt className="m-0 flex flex-col">
                          <span className="text-sm text-[#2B4752]">
                            {fieldLabel(field.name)}
                          </span>
                          <span className="text-[11px] text-[#8FA5A0]">
                            {field.name}
                          </span>
                        </dt>
                        <dd className="m-0 flex items-center gap-2">
                          {field.material !== field.default_material && (
                            <span className="text-[11px] text-[#8FA5A0]">
                              {field.default_material
                                ? "default: material"
                                : "default: logged"}
                            </span>
                          )}
                          <Switch
                            size="small"
                            disabled={!editable}
                            checked={chosen.has(field.name)}
                            onChange={(value) =>
                              toggleField(domain.key, field.name, value)
                            }
                            aria-label={`${fieldLabel(field.name)} reopens review`}
                          />
                        </dd>
                      </div>
                    ))}
                  </dl>
                  <div className="flex items-center justify-between gap-3">
                    {editable && !domain.is_default && (
                      <Button size="small" onClick={() => resetDomain(domain)}>
                        Reset to Vivo default
                      </Button>
                    )}
                  </div>
                </div>
              ),
            };
          })}
        />
      </section>

      {editable && (
        <div className="flex flex-col gap-3 border-t border-[#E6ECF1] pt-4">
          <div className="flex flex-wrap items-center justify-end gap-3">
            {emptyDomains.length > 0 && (
              <span className="text-xs text-[#A31D5C]">
                {emptyDomains.map((d) => d.label).join(", ")} would have no
                material fields, so no change to those records could reopen
                review.
              </span>
            )}
            {dirty && (
              <>
                <span className="mr-auto text-xs text-[#556C75]">
                  Unsaved changes
                </span>
                <Button onClick={() => setDraft(null)}>Discard changes</Button>
              </>
            )}
            <Button
              type="primary"
              disabled={!dirty || emptyDomains.length > 0}
              loading={saveMutation.isPending}
              onClick={() => setConfirming(true)}
            >
              Save policy
            </Button>
          </div>
        </div>
      )}

      <Modal
        title="Apply these changes?"
        open={confirming}
        onCancel={() => setConfirming(false)}
        okText="Apply changes"
        okButtonProps={{
          // A trial already running to a policy needs the change justified;
          // the very first one has nothing to have changed from.
          disabled: amendsExisting && !reason.trim(),
          loading: saveMutation.isPending,
        }}
        onOk={() => saveMutation.mutate()}
        destroyOnHidden
      >
        <div className="flex flex-col gap-3 py-1">
          <ChangeSummary changes={pendingChanges} />
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-[#2B4752]">
              {amendsExisting ? "Reason for this change" : "Note (optional)"}
            </span>
            <Input.TextArea
              rows={3}
              maxLength={2000}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="e.g. Protocol amendment 3 — narrative edits now require re-review"
            />
          </label>
        </div>
      </Modal>
    </div>
  );
}

function ChangeSummary({ changes }: { changes: string[] }) {
  if (changes.length === 0) return null;
  return (
    <div className="rounded-lg border border-[#E6ECF1] bg-[#FAFCFD] px-3 py-2">
      <span className="text-xs font-medium text-[#2B4752]">
        {changes.length} change{changes.length === 1 ? "" : "s"}
      </span>
      <ul className="m-0 mt-1 list-disc pl-4 text-xs text-[#556C75]">
        {changes.map((change) => (
          <li key={change}>{change}</li>
        ))}
      </ul>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="m-0 text-xs font-semibold tracking-wider text-[#556C75] uppercase">
      {children}
    </h3>
  );
}
